import { Switch, Route } from 'react-router-dom';
import { routerActions } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import React from 'react';
import { DATA_TYPES } from 'platform/common/dataType';
import { TableCell, nonNil } from 'platform/common/common.type';
import { Website } from 'platform/adminPanel/websites/website.type';
import { fetchTeaserTemplates } from 'platform/adminPanel/masterTemplates/masterTemplates.service';
import { fetchWebsites } from 'platform/adminPanel/websites/website.service';
import { includesIgnoreCase } from 'platform/common/utils/filter.util';
import { usePromise } from 'platform/common/hooks/usePromise';
import FormattedTable from 'platform/common/components/FormattedTable/FormattedTable';
import InlineDropdown from 'platform/common/components/InlineDropdown/InlineDropdown';
import ListToolbar from 'platform/common/components/ListToolbar/ListToolbar';
import UpdatedOn from 'platform/common/components/UpdatedOn/UpdatedOn';
import { TeaserTemplate } from 'platform/adminPanel/masterTemplates/masterTemplates.type';
import { capitalize } from 'lodash-es';
import { TeaserTemplateCreate, TeaserTemplateEdit } from './TeaserTemplateForm';

type TeaserTemplateWithWebsite = TeaserTemplate & {
    website: Website;
};

const fetchTeaserTemplatesWithWebsites = async (): Promise<TeaserTemplateWithWebsite[]> => {
    const [templates, websites] = await Promise.all([fetchTeaserTemplates(), fetchWebsites()]);
    return templates.map(t => ({
        ...t,
        website: nonNil(websites.find(w => t.websiteId === w.id)),
    }));
};

const TeaserTemplateList = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = React.useState('');

    const [{ data, loading }, refetch] = usePromise([], fetchTeaserTemplatesWithWebsites, []);

    const teaserTemplates = data.filter(t => includesIgnoreCase(t.name, searchQuery));

    const columnDefinitions = [
        {
            Header: 'ID',
            accessor: (t: TeaserTemplateWithWebsite) => t.id,
            type: DATA_TYPES.ID,
            width: 80,
        },
        {
            Header: 'Name',
            accessor: (t: TeaserTemplateWithWebsite) => t.name,
            type: DATA_TYPES.TEXT,
            minWidth: 200,
        },
        {
            Header: 'Format',
            accessor: (t: TeaserTemplateWithWebsite) => capitalize(t.formatType),
            type: DATA_TYPES.TEXT,
            minWidth: 100,
        },
        {
            Header: 'Website',
            accessor: (t: TeaserTemplateWithWebsite) => t.website.name,
            type: DATA_TYPES.TEXT,
            minWidth: 250,
        },
        {
            Header: 'Edited',
            accessor: 'lastModifiedOn',
            width: 160,
            Cell: (cell: TableCell<TeaserTemplateWithWebsite>) => (
                <UpdatedOn date={cell.original.lastModifiedOn} updatedBy={cell.original.lastModifiedBy} />
            ),
        },
        {
            className: () => 'edit-dropdown pull-right cell-align-right',
            width: 50,
            sortable: false,
            Cell: (cell: TableCell<TeaserTemplateWithWebsite>) => (
                <InlineDropdown
                    items={[
                        {
                            label: 'Edit',
                            action: () =>
                                dispatch(
                                    routerActions.push(
                                        `/admin-panel/master-templates/teaser/edit/${cell.original.id}`
                                    )
                                ),
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <div>
            <div className="mb-4">
                <ListToolbar
                    value={searchQuery}
                    onCreate={() => {
                        dispatch(routerActions.push('/admin-panel/master-templates/teaser/new'));
                    }}
                    onSearch={setSearchQuery}
                />
            </div>
            <FormattedTable
                className="-striped"
                columns={columnDefinitions}
                data={teaserTemplates}
                loading={loading}
            />
            <Switch>
                <Route
                    path={'/admin-panel/master-templates/teaser/new'}
                    render={props => (
                        <TeaserTemplateCreate
                            {...props}
                            redirectTo={'/admin-panel/master-templates/teaser'}
                            canEdit
                            afterSubmit={(teaserTemplate: TeaserTemplate) =>
                                dispatch(
                                    routerActions.push(
                                        `/admin-panel/master-templates/teaser/edit/${teaserTemplate.id}`
                                    )
                                )
                            }
                        />
                    )}
                />
                <Route
                    path={'/admin-panel/master-templates/teaser/edit/:id'}
                    render={props => (
                        <TeaserTemplateEdit
                            {...props}
                            redirectTo={'/admin-panel/master-templates/teaser'}
                            canEdit
                            onClose={refetch}
                        />
                    )}
                />
            </Switch>
        </div>
    );
};

export default TeaserTemplateList;
